import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router";
import Alert from "@/components/ui/alert/Alert";

declare global {
  interface Window {
    AuraSurvey: any;
  }
}

const API_URL = import.meta.env.VITE_API_URL_BACKEND;

export default function TakeSurvey() {
  const { tenant, id } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const surveyInstanceRef = useRef<any>(null);
  const surveyMetaRef = useRef<any>(null);

  const [surveyJSON, setSurveyJSON] = useState<any>(null);
  const [surveyMeta, setSurveyMeta] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    surveyMetaRef.current = surveyMeta;
  }, [surveyMeta]);

  useEffect(() => {
    if (!id || !tenant) {
      setError("Falta el tenant o el código de la encuesta en la URL.");
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const fetchSurvey = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${API_URL}/survey/find-response/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-id": tenant,
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          const err = await response.json().catch(() => null);
          throw new Error(
            err?.message ||
              "No se pudo cargar la encuesta. Verifica el código.",
          );
        }

        const data = await response.json();

        setSurveyJSON(data.survey || data.json || data);
        setSurveyMeta({
          nombre: data.nombre,
          descripcion: data.descripcion,
          codigo: data.codigo || id,
          id: data.id,
        });
      } catch (err: any) {
        if (err.name !== "AbortError") {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSurvey();

    return () => controller.abort();
  }, [id, tenant]);

  useEffect(() => {
    if (!surveyJSON) return;
    if (!containerRef.current) return;

    const loadCss = (href: string) => {
      if (document.querySelector(`link[href="${href}"]`)) return;
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    };
    loadCss("/libs/aura-survey/css/aura-survey.css");

    const loadJs = (src: string) =>
      new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(
          `script[src="${src}"]`,
        );
        if (existing) {
          if ((existing as any).dataset.loaded) return resolve();
          existing.addEventListener("load", () => resolve());
          existing.addEventListener("error", () =>
            reject(new Error(`No se encontró ${src}`)),
          );
          return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => {
          (script as any).dataset.loaded = "true";
          resolve();
        };
        script.onerror = () => reject(new Error(`No se encontró ${src}`));
        document.body.appendChild(script);
      });

    let surveyInstance: any;

    loadJs("/libs/aura-survey/js/aura-survey.js")
      .then(() => {
        if (!containerRef.current) return;
        if (!window.AuraSurvey) throw new Error("AuraSurvey no cargó");

        surveyInstanceRef.current?.destroy?.();

        surveyInstance = new window.AuraSurvey(
          containerRef.current,
          surveyJSON,
        );

        surveyInstance.on("complete", async (results: any) => {
          try {
            setSending(true);
            setError(null);

            const meta = surveyMetaRef.current;

            const response = await fetch(`${API_URL}/survey-response/create`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "x-tenant-id": tenant!,
              },
              body: JSON.stringify({
                survey_id: meta?.id ? Number(meta.id) : undefined,
                codigo: meta?.codigo || id,
                response: results,
              }),
            });

            if (!response.ok) {
              const errData = await response.json().catch(() => null);
              throw new Error(
                errData?.message || "Error al enviar las respuestas",
              );
            }
          } catch (err: any) {
            console.error(err);
            setError(err.message);
          } finally {
            setSending(false);
          }
        });

        surveyInstanceRef.current = surveyInstance;
      })
      .catch((err) => setError(err.message));

    return () => {
      surveyInstance?.destroy?.();
      surveyInstanceRef.current = null;
    };
  }, [surveyJSON]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h- bg-gray-50 dark:bg-gray-900 rounded-2xl">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {error && (
        <div className="mb-6">
          <Alert variant="error" title="Error" message={error} />
        </div>
      )}

      {sending && (
        <div className="mb-6">
          <Alert
            variant="info"
            title="Enviando"
            message="Guardando tus respuestas..."
          />
        </div>
      )}

      <div ref={containerRef} className="min-h- w-full" />
    </div>
  );
}
